import pick from 'object.pick'
import type { ChangeEventHandler, FocusEventHandler, ReactElement } from 'react'
import React, { useEffect, useState } from 'react'

import TextFieldContainer from '@ui-kit/components/formComponents/TextFieldContainer/index.web'
import { textInput } from '@ui-kit/components/formComponents/TextFieldContainer/styles'
import { FORM_ELEMENT } from '@ui-kit/constants'

import type { TextFieldProps } from './types'

const TextField = (props: TextFieldProps): ReactElement => {
  // Input Events Props
  const { onFocus, onBlur, onValueChange, onChange, onMouseDown, onKeyPress, onKeyDown } = props

  // Accessibility Props
  const accessibilityProps = pick(props, [
    'aria-controls',
    'aria-activedescendant',
    'aria-autocomplete',
  ])

  const [isFocused, setIsFocused] = useState(props.autoFocus ?? false)
  const [isFilled, setIsFilled] = useState(Boolean(props.value))

  useEffect(() => {
    setIsFilled(Boolean(props.value))
  }, [props.value])

  const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = event.target.value

    if (typeof onChange === 'function') {
      onChange(event)
    }

    if (typeof onValueChange === 'function') {
      onValueChange(value)
    }

    setIsFilled(Boolean(value))
  }

  const onFocusHandler: FocusEventHandler<HTMLInputElement> = (event) => {
    if (props.disabled) {
      return
    }

    setIsFocused(true)

    if (typeof onFocus === 'function') {
      onFocus(event)
    }
  }

  const onBlurHandler: FocusEventHandler<HTMLInputElement> = (event) => {
    setIsFocused(false)

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }

  return (
    <TextFieldContainer
      assistiveText={props.assistiveText}
      disabled={props.disabled}
      errorMessage={props.errorMessage}
      label={props.label}
      name={props.name}
      status={props.status}
      styleType={props.styleType}
      isFocused={isFocused}
      isFilled={isFilled}
      fieldId={props.id}
    >
      <input
        className={textInput({
          color: props.errorMessage ? FORM_ELEMENT.errorColorName : undefined,
        })}
        id={props.id}
        autoComplete={props.autoComplete}
        disabled={props.disabled}
        name={props.name}
        placeholder={props.placeholder}
        ref={props.fieldRef}
        type={props.type ?? (props.keyboardType === 'email-address' ? 'email' : undefined)}
        value={props.value}
        onChange={onChangeHandler}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        onMouseDown={onMouseDown}
        autoFocus={props.autoFocus}
        {...accessibilityProps}
      />
    </TextFieldContainer>
  )
}

export default TextField
